/**
 * Opening-hours flyout on the Gastgeber detail page
 * (inc/gastgeber-opening-hours.php): disclosure button + WAI-ARIA tabs.
 *
 * Works like the footer pills (footer-tooltip.js) — click toggles, Escape and
 * a click outside close — plus what the footer lacks: aria-controls, a close
 * button, closing when keyboard focus leaves, and arrow keys in the tabs.
 */

const OPEN_CLASS = "is-open";

function setOpen(wrapper, open, { focusToggle = false } = {}) {
  const toggle = wrapper.querySelector("[aria-controls]");
  const flyout = toggle && document.getElementById(toggle.getAttribute("aria-controls"));

  if (!toggle || !flyout) {
    return;
  }

  wrapper.classList.toggle(OPEN_CLASS, open);
  toggle.setAttribute("aria-expanded", open ? "true" : "false");
  flyout.hidden = !open;

  if (!open && focusToggle) {
    toggle.focus();
  }
}

function initFlyout(wrapper) {
  const toggle = wrapper.querySelector("[aria-controls]");
  const close = wrapper.querySelector(".js-gastgeber-hours-close");

  if (!toggle) {
    return;
  }

  toggle.addEventListener("click", () => {
    setOpen(wrapper, !wrapper.classList.contains(OPEN_CLASS));
  });

  if (close) {
    close.addEventListener("click", () => setOpen(wrapper, false, { focusToggle: true }));
  }

  wrapper.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && wrapper.classList.contains(OPEN_CLASS)) {
      event.stopPropagation();
      setOpen(wrapper, false, { focusToggle: true });
    }
  });

  // Tabbing past the last link closes it, so it never lingers over the page.
  wrapper.addEventListener("focusout", (event) => {
    if (event.relatedTarget && !wrapper.contains(event.relatedTarget)) {
      setOpen(wrapper, false);
    }
  });

  document.addEventListener("click", (event) => {
    if (wrapper.classList.contains(OPEN_CLASS) && !wrapper.contains(event.target)) {
      setOpen(wrapper, false);
    }
  });
}

function initTabs(container) {
  const tabs = Array.from(container.querySelectorAll('[role="tab"]'));

  const select = (tab, focus) => {
    tabs.forEach((other) => {
      const selected = other === tab;
      const panel = document.getElementById(other.getAttribute("aria-controls"));

      other.setAttribute("aria-selected", selected ? "true" : "false");
      other.tabIndex = selected ? 0 : -1;

      if (panel) {
        panel.hidden = !selected;
      }
    });

    if (focus) {
      tab.focus();
    }
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => select(tab, false));

    tab.addEventListener("keydown", (event) => {
      let next = null;

      if (event.key === "ArrowRight") {
        next = tabs[(index + 1) % tabs.length];
      } else if (event.key === "ArrowLeft") {
        next = tabs[(index - 1 + tabs.length) % tabs.length];
      } else if (event.key === "Home") {
        next = tabs[0];
      } else if (event.key === "End") {
        next = tabs[tabs.length - 1];
      }

      if (next) {
        event.preventDefault();
        select(next, true);
      }
    });
  });
}

function init() {
  document.querySelectorAll(".js-gastgeber-hours").forEach(initFlyout);
  document.querySelectorAll("[data-gastgeber-hours-tabs]").forEach(initTabs);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
